import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Apple, Smartphone, Monitor, Download, Share, Plus, Check, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
};

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Macintosh|Windows|Linux/i.test(ua)) return "desktop";
  return "unknown";
};

const Step = ({ n, children }) => (
  <li className="flex gap-3 items-start">
    <span className="mt-0.5 shrink-0 w-6 h-6 grid place-items-center rounded-full border border-gold/50 text-gold font-condensed text-xs">
      {n}
    </span>
    <span className="font-body text-sm text-neutral-200 leading-relaxed">{children}</span>
  </li>
);

const PlatformCard = ({ id, active, icon: Icon, title, subtitle, children }) => (
  <div
    data-testid={`install-card-${id}`}
    className={`bg-jet-surface rounded-lg overflow-hidden transition-all duration-300 ${
      active ? "border-2 border-gold shadow-[0_0_40px_rgba(212,175,55,0.15)]" : "border border-gold/20"
    }`}
  >
    <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-900">
      <div className="w-10 h-10 grid place-items-center rounded border-gold-gradient">
        <Icon className="w-5 h-5 text-gold" />
      </div>
      <div>
        <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">
          {subtitle}
        </div>
        <div className="font-display text-2xl text-white uppercase leading-none">{title}</div>
      </div>
      {active && (
        <span className="ml-auto font-condensed uppercase tracking-widest text-[10px] text-crimson-live">
          ● You're here
        </span>
      )}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InstallPage = () => {
  const [platform, setPlatform] = useState("unknown");
  const [installed, setInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setInstalled(isStandalone());
    setCanInstall(Boolean(window.__gfnDeferredInstall));

    const onAvail = () => setCanInstall(true);
    const onDone = () => {
      setCanInstall(false);
      setInstalled(true);
      toast.success("GFN Soul NYC installed 🎉");
    };
    window.addEventListener("gfn-install-available", onAvail);
    window.addEventListener("gfn-install-done", onDone);
    return () => {
      window.removeEventListener("gfn-install-available", onAvail);
      window.removeEventListener("gfn-install-done", onDone);
    };
  }, []);

  const doInstall = async () => {
    const evt = window.__gfnDeferredInstall;
    if (!evt) {
      toast.error("Install isn't available in this browser. Follow the steps below.");
      return;
    }
    evt.prompt();
    try {
      const choice = await evt.userChoice;
      if (choice?.outcome === "accepted") {
        setInstalled(true);
      }
    } catch (_) {
      /* ignore */
    }
    window.__gfnDeferredInstall = null;
    setCanInstall(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10">
      {/* Hero */}
      <header className="grid md:grid-cols-[1.4fr_1fr] gap-8 items-center mb-10">
        <div>
          <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
            <Zap className="w-3.5 h-3.5 text-gold" />
            <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
              Install The App
            </span>
          </div>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] text-white uppercase">
            Take GFN <span className="text-gold-gradient">everywhere</span>
          </h1>
          <p className="mt-4 max-w-xl font-body text-neutral-300 text-base md:text-lg">
            Install GFN Soul NYC as an app on your phone, tablet, or desktop. Full-screen,
            no browser bar, one-tap access to two radio stations and the live TV network.
            No store, no signup, no download fees.
          </p>
          {installed ? (
            <div
              data-testid="install-already"
              className="mt-6 inline-flex items-center gap-2 border border-gold/40 rounded-full px-5 py-3 bg-gold/10 text-gold font-condensed uppercase tracking-widest text-xs"
            >
              <Check className="w-4 h-4" />
              Already installed on this device
            </div>
          ) : (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="install-primary-btn"
                onClick={doInstall}
                disabled={!canInstall}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
              >
                <Download className="w-4 h-4" />
                {canInstall ? "Install Now" : "Install (see steps below)"}
              </button>
              <Link
                to="/"
                data-testid="install-back-home"
                className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
              >
                <ArrowRight className="w-4 h-4" />
                Back to Stations
              </Link>
            </div>
          )}
        </div>

        {/* App icon showcase */}
        <div className="grid place-items-center">
          <div className="relative">
            <div className="absolute -inset-6 bg-gold/20 blur-3xl rounded-full" aria-hidden />
            <img
              src="/icon-512.png"
              alt="GFN Soul NYC app icon"
              className="relative w-56 h-56 md:w-64 md:h-64 rounded-3xl shadow-[0_0_60px_rgba(212,175,55,0.35)]"
              data-testid="install-app-icon"
            />
          </div>
        </div>
      </header>

      {/* Perks strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {[
          { t: "Full-Screen", s: "No browser bar" },
          { t: "Home Screen", s: "One-tap launch" },
          { t: "Persistent Player", s: "Keeps playing" },
          { t: "Free Forever", s: "No store, no fees" },
        ].map((p) => (
          <div
            key={p.t}
            className="rounded-lg border border-gold/20 bg-jet-surface/60 px-4 py-3 text-center"
          >
            <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">
              {p.t}
            </div>
            <div className="font-body text-xs text-neutral-400 mt-0.5">{p.s}</div>
          </div>
        ))}
      </section>

      {/* Platform install cards */}
      <section className="grid md:grid-cols-3 gap-4 mb-10">
        <PlatformCard
          id="ios"
          active={platform === "ios"}
          icon={Apple}
          title="iPhone / iPad"
          subtitle="iOS · Safari"
        >
          <ol className="space-y-3">
            <Step n={1}>
              Open <span className="text-gold">gfnsoulglobalent.com</span> in Safari
              (Chrome on iOS can't install PWAs — Apple only).
            </Step>
            <Step n={2}>
              Tap the <Share className="inline w-3.5 h-3.5 text-gold" /> <b>Share</b> button
              at the bottom of the screen.
            </Step>
            <Step n={3}>
              Scroll and tap <b className="text-gold">Add to Home Screen</b>.
            </Step>
            <Step n={4}>
              Tap <b>Add</b> in the top-right. Done — GFN Soul NYC now lives on your home
              screen.
            </Step>
          </ol>
        </PlatformCard>

        <PlatformCard
          id="android"
          active={platform === "android"}
          icon={Smartphone}
          title="Android"
          subtitle="Chrome · Edge · Samsung Internet"
        >
          <ol className="space-y-3">
            <Step n={1}>
              Open <span className="text-gold">gfnsoulglobalent.com</span> in Chrome
              (or Edge / Samsung Internet).
            </Step>
            <Step n={2}>
              Tap the <b>⋮ menu</b> in the top-right corner.
            </Step>
            <Step n={3}>
              Tap <b className="text-gold">Install app</b> (or "Add to Home screen").
            </Step>
            <Step n={4}>
              Confirm <b>Install</b>. The app installs like any other — icon on your home
              screen and app drawer.
            </Step>
          </ol>
        </PlatformCard>

        <PlatformCard
          id="desktop"
          active={platform === "desktop"}
          icon={Monitor}
          title="Desktop"
          subtitle="Chrome · Edge · Brave"
        >
          <ol className="space-y-3">
            <Step n={1}>
              Open <span className="text-gold">gfnsoulglobalent.com</span> in Chrome
              (or Edge / Brave).
            </Step>
            <Step n={2}>
              Look for the <b>install</b> icon <Plus className="inline w-3.5 h-3.5 text-gold" />
              in the address bar (right side).
            </Step>
            <Step n={3}>
              Or open the menu → <b className="text-gold">Install GFN Soul NYC…</b>
            </Step>
            <Step n={4}>
              GFN launches as a real desktop app window — pin it to the taskbar / dock.
            </Step>
          </ol>
        </PlatformCard>
      </section>

      {/* FAQ */}
      <section className="rounded-lg border border-gold/20 bg-jet-surface/60 p-6 md:p-8">
        <h2 className="font-display text-3xl uppercase text-white mb-6">
          Frequently <span className="text-gold-gradient">asked</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
              Is this from the App Store or Play Store?
            </div>
            <p className="font-body text-sm text-neutral-300 mt-1">
              No — GFN Soul NYC is a Progressive Web App. It installs straight from the
              website, works exactly like a native app, and never goes through Apple or
              Google's approval process.
            </p>
          </div>
          <div>
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
              Will it work offline?
            </div>
            <p className="font-body text-sm text-neutral-300 mt-1">
              The app shell (interface + saved settings) opens offline. Live radio and
              TV need an internet connection — they stream in real-time.
            </p>
          </div>
          <div>
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
              How do I update it?
            </div>
            <p className="font-body text-sm text-neutral-300 mt-1">
              Updates arrive automatically the next time you open the app. No store
              refresh, no manual download.
            </p>
          </div>
          <div>
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
              How do I uninstall?
            </div>
            <p className="font-body text-sm text-neutral-300 mt-1">
              Same as any app — long-press the icon and remove, or use your device's
              standard uninstall flow. On desktop, right-click the app window → Uninstall.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstallPage;
